import { Link } from "react-router-dom";
import { ArrowRight, Play, Music4, Shirt, Clapperboard, Camera, Radio } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeIn, Tag } from "@/components/Motion";
import { IMAGES, MASCOT_URL, PANELS } from "@/lib/brand";

const SLATE = [
    { icon: Play, tag: "Watch", status: "In Production", title: "Video & Film", copy: "Original video content, behind-the-build documentaries, and community reels.", img: PANELS.director },
    { icon: Music4, tag: "Listen", status: "In Production", title: "Original Music", copy: "Tracks and soundscapes born inside the Network — the sound of the lane.", img: PANELS.studio },
    { icon: Shirt, tag: "Lookbook", status: "Coming Soon", title: "Fashion & Streetwear", copy: "The PURP ALERT aesthetic on bodies, not mood boards. Caps, chains, jerseys.", img: MASCOT_URL },
    { icon: Clapperboard, tag: "Concepts", status: "Coming Soon", title: "TV & Film Projects", copy: "Long-form storytelling in development — series concepts and scripts with PURP DNA.", img: IMAGES.creatorDesk },
    { icon: Camera, tag: "UGC", status: "Open Lane", title: "UGC & Creator Content", copy: "Community-made content with real direction — an open lane for creators in the Network.", img: PANELS.puzzle },
    { icon: Radio, tag: "Social / Live", status: "Rolling", title: "Social & Live", copy: "MY 5 drops, live sessions, and real-time community energy across platforms.", img: PANELS.stage },
];

export default function Media() {
    return (
        <div data-testid="media-page">
            <PageHero
                eyebrow="PURP ALERT Media"
                titleLines={["The studio side", "of the Network."]}
                copy="Video, original music, fashion, TV & film concepts, UGC, and social — a media brand being built in public. The slate is in active production; first drops are on the way."
            />

            <section className="py-16 md:py-24 border-t border-white/10" data-testid="media-slate">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {SLATE.map((m, i) => (
                            <FadeIn key={m.title} delay={0.07 * i}>
                                <div className="group h-full rounded-2xl overflow-hidden border border-white/10 hover:border-purp/50 transition-colors duration-300 bg-panel/75" data-testid={`media-slate-card-${i}`}>
                                    <div className="relative aspect-[16/10] overflow-hidden">
                                        <img src={m.img} alt={m.title} className="w-full h-full object-cover opacity-75 group-hover:opacity-95 group-hover:scale-105 transition-all duration-700" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-panel to-transparent" />
                                        <div className="absolute top-4 left-4 flex gap-2">
                                            <Tag>{m.tag}</Tag>
                                            <Tag tone={m.status === "Rolling" ? "volt" : "muted"}>{m.status}</Tag>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center justify-between">
                                            <h3 className="font-display font-extrabold uppercase text-2xl">{m.title}</h3>
                                            <m.icon className="w-5 h-5 text-purp-neon" />
                                        </div>
                                        <p className="mt-3 text-sm text-slate-400 leading-relaxed">{m.copy}</p>
                                    </div>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32 border-t border-white/10 relative overflow-hidden" data-testid="media-cta">
                <div className="absolute inset-0 bg-radial-purp" />
                <div className="relative max-w-3xl mx-auto px-5 md:px-8 text-center">
                    <FadeIn>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Make media <span className="italic text-purp text-glow">with the Network.</span>
                        </h2>
                        <p className="mt-6 text-slate-400 text-base md:text-lg max-w-xl mx-auto">
                            Shooters, editors, artists, hosts, UGC creators — the media slate needs your lane.
                        </p>
                        <Link to="/get-involved" data-testid="media-get-involved-cta" className="mt-9 inline-flex items-center gap-2 rounded-full bg-purp px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_44px_rgba(168,85,247,0.7)]">
                            Collaborate <ArrowRight className="w-4 h-4" />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
}
